// The Areas tab's zones, matched to the drawing under the model, and that
// drawing with the zones painted on (owner, 2026-10-09: "make sure we can
// select the areas sheet, with its areas colors added to it").
//
// WHICH ZONES BELONG TO A SHEET PAGE. A zone is drawn on one page IMAGE
// (coordinationArea.sheetImageUrl, one of docControl.pages) and its points
// are percentages of that image. Prod SpaceX BA03 has zones that carry the
// document id and zones that carry only the image (six on page 2 have no
// sheetDocControlId at all), so the image is matched first and the id is
// the fallback for a zone that has no image.
import { useEffect, useMemo, useState } from "react";
import { url } from "api";
import { listAreas } from "api/areas/areasApi";
import { composeAreaSheet } from "./areaSheetPaint";

const pathOf = (u) => {
  const s = String(u || "").split("?")[0];
  const i = s.indexOf("/uploads/");
  return i >= 0 ? s.slice(i) : s;
};
const abs = (u) => (!u ? "" : /^(https?:|data:|blob:)/i.test(u) ? u : `${url}${u.startsWith("/") ? "" : "/"}${u}`);

export const pageImageOf = (sheet, page = 1) => {
  const pages = (sheet && Array.isArray(sheet.pages) ? sheet.pages : []).filter(Boolean);
  return pages[page - 1] || "";
};

// The zones drawn on page `page` of `sheet`.
export function areasOnSheet(areas, sheet, page = 1) {
  if (!sheet) return [];
  const img = pathOf(pageImageOf(sheet, page));
  const id = String(sheet._id);
  return (areas || []).filter((a) => {
    if (!a || !Array.isArray(a.points) || a.points.length < 3) return false;
    if (a.sheetImageUrl) return !!img && pathOf(a.sheetImageUrl) === img;
    return page === 1 && a.sheetDocControlId && String(a.sheetDocControlId) === id;
  });
}

// Every sheet page that has zones on it, for the "Areas sheet" picker:
// [{ sheetId, page, count, levels }]. A zone whose image is on no listed
// document cannot be placed on a tracking page (a page stores a document),
// so it is counted in `unplaced` rather than silently left out.
export function areaSheetPages(areas, sheets) {
  const byImg = new Map();
  for (const s of sheets || []) {
    (Array.isArray(s.pages) ? s.pages : []).forEach((p, i) => { if (p) byImg.set(pathOf(p), { sheetId: String(s._id), page: i + 1 }); });
  }
  const out = new Map();
  let unplaced = 0;
  for (const a of areas || []) {
    if (!a || !Array.isArray(a.points) || a.points.length < 3) continue;
    let hit = a.sheetImageUrl ? byImg.get(pathOf(a.sheetImageUrl)) : null;
    if (!hit && !a.sheetImageUrl && a.sheetDocControlId && (sheets || []).some((s) => String(s._id) === String(a.sheetDocControlId))) {
      hit = { sheetId: String(a.sheetDocControlId), page: 1 };
    }
    if (!hit) { unplaced += 1; continue; }
    const k = `${hit.sheetId}|${hit.page}`;
    const row = out.get(k) || { ...hit, count: 0, levels: new Set() };
    row.count += 1;
    if (a.level) row.levels.add(a.level);
    out.set(k, row);
  }
  return {
    pages: [...out.values()].map((r) => ({ ...r, levels: [...r.levels].sort() })),
    unplaced,
  };
}

// Loads the project's zones (once per project) and, while `on`, paints
// the ones on this sheet page into the drawing. Returns the painted image
// (a data URL) or "" when there is nothing to paint, plus the zones.
export default function useAreasSheet(projectId, sheet, page, { on = false, enabled = true } = {}) {
  const [areas, setAreas] = useState([]);
  const [img, setImg] = useState("");
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    listAreas(projectId)
      .then((r) => { if (alive) setAreas((r && r.areas) || []); })
      .catch(() => { if (alive) setAreas([]); });
    return () => { alive = false; };
  }, [projectId]);
  const here = useMemo(() => areasOnSheet(areas, sheet, page), [areas, sheet, page]);
  // The picture the zones were drawn on - the page image itself, never a
  // second rasterisation of the PDF, so the percentages land on the exact
  // pixels they were measured against.
  const base = useMemo(() => {
    const withImg = here.find((a) => a.sheetImageUrl);
    return withImg ? abs(pathOf(withImg.sheetImageUrl)) : abs(pageImageOf(sheet, page));
  }, [here, sheet, page]);
  const key = here.map((a) => `${a._id}:${a.color}:${a.opacity}:${(a.points || []).length}`).join(",");
  useEffect(() => {
    let alive = true;
    setImg("");
    if (!on || !enabled || !here.length || !base) return undefined;
    composeAreaSheet(base, here)
      .then((d) => { if (alive) setImg(d); })
      .catch(() => { /* the plain sheet still draws */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, enabled, base, key]);
  return { areas, here, img };
}
